import { useState } from 'react';
import { Button, Col, Form, Image, Input, Modal, message } from 'antd';
import { TextService } from '../../Services/TextService';
import { BookyInput } from '../../Components/Forms/BookyInput';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import { OneFrontHtmlEditor } from '../../Components/Forms/OneFrontHtmlEditor';
import { BookyCheckbox } from '../../Components/Forms/BookyCheckbox';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { EmailTemplateType } from '../../Types/EmailTemplate';
import ImageSelect from '../../Components/Select/ImageSelect';
import EmailDesignTemplateCardSelect from '../../Components/Select/EmailDesignTemplateCardSelect';
import { BlobDto } from '../../Repository/eventbookyapi';
import { DocumentService } from '../../Services/ImageService';
import ColorPicker from '../../Components/Color/ColorPicker';

const DEFAULT_ACCENT_COLOR = "#4a6cf7";
const DEFAULT_TEXT_COLOR = "#ffffff";

export const CreateEmailTemplateButton = (props: any) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [html, setHtml] = useState("" as string);
    const [images, setImages] = useState([] as BlobDto[]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false as boolean);
    const [selectedLogo, setSelectedLogo] = useState("" as string);
    const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR as string);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR as string);

    const GetText = TextService.GetText;
    const hasEmailDesign = Form.useWatch("enableEmailDesign", form) === true;
    const selectedEmailDesignTemplate = Form.useWatch("emailDesignTemplate", form) as EmailTemplateType | undefined;

    const onCancel = () => {
        clearForm();
        setOpen(false);
    };

    const onHtmlChange = (value: string) => {
        setHtml(value);
        form.setFieldValue("body", value);
    }

    const loadImages = async () => {
        const publicImages = await DocumentService.GetPublicImages();
        setImages(publicImages);
    }

    const openImagePicker = async () => {
        await loadImages();
        setIsImageModalOpen(true);
    }

    const onImageSelect = (image: string) => {
        setSelectedLogo(image);
        form.setFieldValue("imageUrl", image);
    }

    const onEmailDesignToggle = (event: any) => {
        if (event?.target?.checked !== true) {
            form.setFieldValue("emailDesignTemplate", undefined);
            form.setFieldValue("header", undefined);
            form.setFieldValue("imageUrl", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setSelectedLogo("");
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
            return;
        }

        form.setFieldValue("emailDesignTemplate", EmailTemplateType.Classic);
    }

    const onEmailDesignTemplateChange = (templateType: EmailTemplateType) => {
        if (templateType !== EmailTemplateType.Classic) {
            form.setFieldValue("imageUrl", undefined);
            setSelectedLogo("");
        }

        if (templateType !== EmailTemplateType.Header) {
            form.setFieldValue("header", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }

        if (templateType === EmailTemplateType.Header) {
            const initialAccentColor = form.getFieldValue("accentColor") || DEFAULT_ACCENT_COLOR;
            const initialTextColor = form.getFieldValue("textColor") || DEFAULT_TEXT_COLOR;
            form.setFieldValue("accentColor", initialAccentColor);
            form.setFieldValue("textColor", initialTextColor);
            setAccentColor(initialAccentColor);
            setTextColor(initialTextColor);
        }
    }

    const onAccentColorChanged = (hex: string) => {
        setAccentColor(hex);
        form.setFieldValue("accentColor", hex);
    }

    const onTextColorChanged = (hex: string) => {
        setTextColor(hex);
        form.setFieldValue("textColor", hex);
    }

    const onCreate = async (values: any) => {
        const includeDesign = values.enableEmailDesign === true;
        const emailDesignTemplate = includeDesign ? values.emailDesignTemplate : undefined;
        const isClassicTemplate = emailDesignTemplate === EmailTemplateType.Classic;
        const isHeaderTemplate = emailDesignTemplate === EmailTemplateType.Header;
        const design = includeDesign
            ? {
                header: isHeaderTemplate ? values.header : undefined,
                imageUrl: isClassicTemplate ? values.imageUrl : undefined,
                emailDesignTemplate: emailDesignTemplate,
                textColor: isHeaderTemplate ? values.textColor : undefined,
                accentColor: isHeaderTemplate ? values.accentColor : undefined,
            }
            : undefined;

        try {
            await EmailTemplateService.CreateTemplate({
                templateName: values.templateName,
                subject: values.subject,
                design: design,
                body: values.body,
                purpose: values.sendWhenUserIsCreated === true ? 1 : 0,
            });
        } catch (error) {
            message.warning(GetText("CreateTemplate_ErrorAlreadyExist"));
            return;
        }

        setOpen(false);
        clearForm();
        props.reloadTemplate();
    }

    const clearForm = () => {
        form.resetFields();
        form.setFieldValue("enableEmailDesign", false);
        form.setFieldValue("sendWhenUserIsCreated", false);
        setHtml("");
        setSelectedLogo("");
        setAccentColor(DEFAULT_ACCENT_COLOR);
        setTextColor(DEFAULT_TEXT_COLOR);
    }

    return (
        <>
            <Modal
                width={900}
                open={open}
                title={GetText("CreateTemplate_ModalHeader")}
                okText={GetText("CreateTemplate_CreateText")}
                cancelText={GetText("CreateTemplate_CancelText")}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
                onCancel={onCancel}
            >
                <Form form={form} onFinish={onCreate} layout='vertical'>
                    <Col span={24}>
                        <BookyInput name="templateName" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_PlaceHolderText")} />
                        <BookyInput name="subject" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Subject")} />
                        <OneFrontHtmlEditor initialHtml={html} enablePaymentLink={props.enablePaymentLink} onHtmlChange={onHtmlChange} label={GetText("CreateTemplate_Body")} name="body" />

                        <BookyCheckbox alignLabelRight label={GetText("CreateTemplate_AddDesign")} name="enableEmailDesign" onChange={onEmailDesignToggle}></BookyCheckbox>

                        {hasEmailDesign ? <>
                            <Form.Item
                                label={GetText("CreateTemplate_DesignTemplate")}
                                name="emailDesignTemplate"
                                rules={[{ required: true, message: GetText("CreateTemplate_RequiredText") }]}
                            >
                                <EmailDesignTemplateCardSelect onTemplateSelected={onEmailDesignTemplateChange} />
                            </Form.Item>

                            {selectedEmailDesignTemplate === EmailTemplateType.Classic ? <>
                                <Form.Item
                                    name="imageUrl"
                                    hidden
                                    dependencies={["enableEmailDesign", "emailDesignTemplate"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue("enableEmailDesign") && getFieldValue("emailDesignTemplate") === EmailTemplateType.Classic && !value) {
                                                    return Promise.reject(new Error(GetText("CreateTemplate_LogoRequired")));
                                                }

                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_Logo")}>
                                    <Button onClick={openImagePicker}>{GetText("CreateTemplate_SelectLogo")}</Button>
                                </Form.Item>

                                {selectedLogo ? <Image width={220} height={140} preview={true} src={selectedLogo}></Image> : ""}
                            </> : ""}

                            {selectedEmailDesignTemplate === EmailTemplateType.Header ? <>
                                <BookyInput name="header" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Header")} />

                                <Form.Item label={GetText("CreateTemplate_AccentColor")}>
                                    <ColorPicker color={accentColor} onChangeComplete={onAccentColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="accentColor">
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_TextColor")}>
                                    <ColorPicker color={textColor} onChangeComplete={onTextColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="textColor">
                                    <Input />
                                </Form.Item>
                            </> : ""}
                        </> : ""}

                        <BookyCheckbox label={GetText("CreateTemplate_NewMemberRegister")} name="sendWhenUserIsCreated"></BookyCheckbox>
                    </Col>
                </Form>
            </Modal >

            <Modal
                title={GetText("MyEventDetails_SelectImage")}
                width={850}
                open={isImageModalOpen}
                onOk={() => { setIsImageModalOpen(false) }}
                onCancel={() => { setIsImageModalOpen(false) }}
            >
                <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={selectedLogo} />
            </Modal>

            <div>
                <BookyButton
                    type="primary"
                    fullWidth={false}
                    text={GetText("CreateTemplate_CreateText")}
                    onClick={() => {
                        clearForm();
                        setOpen(true);
                    }}
                >

                </BookyButton>
            </div>
        </>
    );
};

export default CreateEmailTemplateButton;